import { useMemo } from "react";
import { MVTLayer } from "deck.gl";
import { Feature, Geometry } from "geojson";
import { BackgroundTileZoom, DebounceTimeMilliseconds, MaxConcurrentRequests } from "./useTrajectoriesLayer";
import type { DateTime } from "luxon";
import type { TrajectoryPointDtoVehicleType } from "@/api/generated/models/trajectoryPointDtoVehicleType";
import { useJunctionTileUrl, JunctionTileSource } from "@/hooks/layers/useJunctionTileUrl";
import { DEFAULT_COLOUR, hexToRgb } from "@/utils/colourUtils";
interface SegmentFeatureProperties {
  movement_id: number;
  vehicle_type: TrajectoryPointDtoVehicleType;
}
export function useJunctionMovementSegmentsLayer(junctionId: number | null, vehicleTypes: TrajectoryPointDtoVehicleType[], startTimestamp: DateTime | null, endTimestamp: DateTime | null, colorByMovementId: Map<number, string>, selectedMovementId: number | null, visible: boolean) {
  const tileUrl = useJunctionTileUrl(JunctionTileSource.segments, {
    junctionId,
    startTimestamp,
    endTimestamp
  });
  const vehicleTypeSet = useMemo(() => new Set(vehicleTypes), [vehicleTypes]);
  const getLineColor = (f: Feature<Geometry, SegmentFeatureProperties>) => {
    const hex = colorByMovementId.get(f.properties.movement_id);
    return hex ? hexToRgb(hex) : DEFAULT_COLOUR;
  };
  const getLineWidth = (f_0: Feature<Geometry, SegmentFeatureProperties>) => {
    if (!vehicleTypeSet.has(f_0.properties.vehicle_type)) return 0;
    if (selectedMovementId !== null && f_0.properties.movement_id !== selectedMovementId) {
      return 0;
    }
    return 0.2;
  };
  const updateTriggers = {
    getLineColor: [colorByMovementId, selectedMovementId],
    getLineWidth: [selectedMovementId, vehicleTypeSet]
  };
  const backgroundLayer = useMemo(() => new MVTLayer<SegmentFeatureProperties>({
    id: "junction-movement-segments-background",
    debounceTime: DebounceTimeMilliseconds,
    maxRequests: MaxConcurrentRequests,
    visible,
    // avoid unnecessary requests when layer is not visible
    data: visible ? tileUrl : null,
    minZoom: BackgroundTileZoom,
    maxZoom: BackgroundTileZoom,
    getLineColor,
    getLineWidth,
    updateTriggers
  }), [tileUrl, visible, colorByMovementId, selectedMovementId, vehicleTypeSet]);
  const foregroundLayer = useMemo(() => new MVTLayer<SegmentFeatureProperties>({
    id: "junction-movement-segments",
    debounceTime: DebounceTimeMilliseconds,
    maxRequests: MaxConcurrentRequests,
    visible,
    // avoid unnecessary requests when layer is not visible
    data: visible ? tileUrl : null,
    minZoom: 14,
    maxZoom: 17,
    getLineColor,
    getLineWidth,
    updateTriggers
  }), [tileUrl, visible, colorByMovementId, selectedMovementId, vehicleTypeSet]);
  return useMemo(() => [backgroundLayer, foregroundLayer] as const, [backgroundLayer, foregroundLayer]);
}